import { Component, input, signal, inject, effect } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatIconModule } from '@angular/material/icon';
import { TranslatePipe } from '@ngx-translate/core';
import { ScoreData } from '../../../../shared/models/score/score.model';
import { ScoreService } from '../../../../shared/services/data/score.service';
import { Location } from '../../../../shared/models/Location/Location';

@Component({
  selector: 'app-score',
  standalone: true,
  imports: [CommonModule, MatIconModule, TranslatePipe],
  templateUrl: './score.component.html',
  styleUrl: './score.component.css',
})
export class ScoreComponent {
  public ticketId = input.required<number>();
  public loading = input<boolean>(false);
  public coordinates = input<{ user: [number, number] | null; formulario: [number, number] | null; evidencias: [number, number] | null }>({ user: null, formulario: null, evidencias: null });
  public locationDetail = input<Location | null>(null);

  private scoreService = inject(ScoreService);

  public scoreData = signal<ScoreData | null>(null);
  public locationScore = signal<{ score: string; color: string }>({ score: 'N/A', color: 'primary' });
  public formularioScore = signal<{ score: string; color: string }>({ score: 'N/A', color: 'primary' });
  public evidenciasScore = signal<{ score: string; color: string }>({ score: 'N/A', color: 'primary' });

  constructor() {
    effect(() => {
      const coords = this.coordinates();
      const location = this.locationDetail();
      this.calculateLocationScores(coords, location);
    });
  }

  private calculateLocationScores(
    coords: { user: [number, number] | null; formulario: [number, number] | null; evidencias: [number, number] | null },
    location: Location | null
  ): void {
    const mainLat = location?.Latitude ? Number(location.Latitude) : null;
    const mainLng = location?.Longitude ? Number(location.Longitude) : null;

    if (!mainLat || !mainLng) {
      this.locationScore.set({ score: 'N/A', color: 'primary' });
      this.formularioScore.set({ score: 'N/A', color: 'primary' });
      this.evidenciasScore.set({ score: 'N/A', color: 'primary' });
      return;
    }

    const radius = this.scoreService.radiusMeters();

    if (coords.user) {
      const dist = this.scoreService.getDistance(mainLat, mainLng, coords.user[0], coords.user[1]);
      const score = Math.max(0, Math.min(100, (1 - Math.pow(dist / radius, this.scoreService.SCORE_POWER_FACTOR)) * 100));
      this.locationScore.set(this.getScoreResult(score));
    } else {
      this.locationScore.set({ score: 'N/A', color: 'primary' });
    }

    if (coords.formulario) {
      const dist = this.scoreService.getDistance(mainLat, mainLng, coords.formulario[0], coords.formulario[1]);
      const score = Math.max(0, Math.min(100, (1 - Math.pow(dist / radius, this.scoreService.SCORE_POWER_FACTOR)) * 100));
      this.formularioScore.set(this.getScoreResult(score));
    } else {
      this.formularioScore.set({ score: 'N/A', color: 'primary' });
    }

    if (coords.evidencias) {
      const dist = this.scoreService.getDistance(mainLat, mainLng, coords.evidencias[0], coords.evidencias[1]);
      const score = Math.max(0, Math.min(100, (1 - Math.pow(dist / radius, this.scoreService.SCORE_POWER_FACTOR)) * 100));
      this.evidenciasScore.set(this.getScoreResult(score));
    } else {
      this.evidenciasScore.set({ score: 'N/A', color: 'primary' });
    }
  }

  private getScoreResult(score: number): { score: string; color: string } {
    if (score >= 80) return { score: score.toFixed(0), color: 'success' };
    if (score >= 60) return { score: score.toFixed(0), color: 'warning' };
    return { score: score.toFixed(0), color: 'danger' };
  }

  public getDonutDashArray(): string {
    const score = this.getTotalScoreNumber();
    const circumference = 2 * Math.PI * 54;
    const filled = (score / 100) * circumference;
    return `${filled} ${circumference}`;
  }

  public getDonutColor(): string {
    const score = this.getTotalScoreNumber();
    if (score >= 80) return 'var(--color-snackbar-success)';
    if (score >= 60) return 'var(--color-snackbar-warning)';
    return 'var(--color-snackbar-error)';
  }

  public getTotalScore(): string {
    return this.getTotalScoreNumber().toFixed(0);
  }

  private getTotalScoreNumber(): number {
    const loc = this.locationScore().score !== 'N/A' ? parseFloat(this.locationScore().score) : null;
    const form = this.formularioScore().score !== 'N/A' ? parseFloat(this.formularioScore().score) : null;
    const evid = this.evidenciasScore().score !== 'N/A' ? parseFloat(this.evidenciasScore().score) : null;

    const scores = [loc, form, evid].filter((s): s is number => s !== null);
    if (scores.length === 0) return 0;
    return scores.reduce((a, b) => a + b, 0) / scores.length;
  }
}
